import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  FaBox, FaTruck, FaMapMarkerAlt, FaCalendarAlt, FaExternalLinkAlt,
  FaArrowLeft, FaSpinner, FaExclamationTriangle, FaCheckCircle,
  FaClock, FaEuroSign, FaCoins, FaUser, FaEnvelope, FaPhone,
  FaShoppingCart
} from "react-icons/fa";
import "../styles/OrderDetailPage.css";

export default function OrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchOrderDetail = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await axios.get(
          `https://reanimeitor.com/backend/get_order_detail.php?id=${id}`,
          { withCredentials: true }
        );
        if (res.data.success) {
          setOrder(res.data.order);
        } else {
          setError(res.data.error || "Pedido no encontrado");
        }
      } catch (err) {
        console.error("Error cargando detalle:", err);
        setError("Error de conexión");
      } finally {
        setLoading(false);
      }
    };
    fetchOrderDetail();
  }, [id]);

  const getShippingStatusClass = (status) => {
    switch (status) {
      case 'delivered':
      case 'completed':
        return 'shipping-completed';
      case 'processing':
      case 'shipped':
        return 'shipping-processing';
      case 'pending':
      default:
        return 'shipping-pending';
    }
  };

  if (loading) {
    return (
      <div className="order-detail-loading">
        <div className="loading-spinner-professional">
          <div className="energy-pulse"></div>
          <div className="loading-text">Cargando pedido...</div>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="order-detail-error">
        <div className="error-container">
          <div className="error-icon">⚡</div>
          <h3>Error</h3>
          <p>{error || "Pedido no encontrado"}</p>
          <button className="energy-btn primary" onClick={() => navigate("/account?tab=orders")}>
            <FaArrowLeft /> Volver a Mis Pedidos
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="order-detail-professional">
      {/* Fondo eléctrico igual que AccountPage */}
      <div className="account-energy-field">
        <div className="energy-bolt"></div>
        <div className="energy-bolt"></div>
      </div>

      {/* Header */}
      <div className="order-detail-header">
        <div className="user-welcome">
          <div className="user-avatar">
            <FaBox className="avatar-icon" />
          </div>
          <div className="welcome-text">
            <h1>Pedido #{order.order_number}</h1>
            <p>Detalle completo de tu compra</p>
          </div>
        </div>
        <div className="header-actions">
          <button className="header-btn" onClick={() => navigate("/account?tab=orders")} title="Volver">
            <FaArrowLeft />
          </button>
          {order.tracking_url && (
            <a href={order.tracking_url} target="_blank" rel="noopener noreferrer" className="header-btn" title="Seguimiento GLS">
              <FaExternalLinkAlt />
            </a>
          )}
        </div>
      </div>

      {/* Contenido */}
      <div className="order-detail-container">
        {/* Resumen del pedido */}
        <div className="detail-main-card">
          <div className="detail-card-header">
            <h3><FaShoppingCart /> Resumen del Pedido</h3>
            <span className={`status-badge ${order.status}`}>{order.status_label}</span>
          </div>
          <div className="detail-card-body">
            <div className="detail-row">
              <span><FaCalendarAlt /> Fecha</span>
              <span>{order.fecha_formateada}</span>
            </div>
            <div className="detail-row">
              <span><FaBox /> Producto</span>
              <span className="pack-name-detail">{order.pack_name || "Pack"}</span>
            </div>
            <div className="detail-row">
              <span><FaEuroSign /> Total</span>
              <span className="highlight">€{parseFloat(order.amount_eur).toFixed(2)}</span>
            </div>
       
          </div>
        </div>

        {/* Dirección de envío */}
        <div className="detail-card">
          <div className="detail-card-header">
            <h3><FaMapMarkerAlt /> Dirección de Envío</h3>
          </div>
          <div className="detail-card-body">
            <div className="detail-row">
              <span><FaUser /> Nombre</span>
              <span>{order.customer_name}</span>
            </div>
            <div className="detail-row">
              <span><FaMapMarkerAlt /> Dirección</span>
              <span>{order.customer_address}</span>
            </div>
            <div className="detail-row">
              <span><FaMapMarkerAlt /> Ciudad</span>
              <span>{order.customer_city}, {order.customer_postal_code}</span>
            </div>
            <div className="detail-row">
              <span><FaMapMarkerAlt /> País</span>
              <span>{order.customer_country}</span>
            </div>
            {order.customer_phone && (
              <div className="detail-row">
                <span><FaPhone /> Teléfono</span>
                <span>{order.customer_phone}</span>
              </div>
            )}
            {order.customer_email && (
              <div className="detail-row">
                <span><FaEnvelope /> Email</span>
                <span>{order.customer_email}</span>
              </div>
            )}
          </div>
        </div>

        {/* Seguimiento GLS */}
        <div className="detail-card tracking-detail-card">
          <div className="detail-card-header">
            <h3><FaTruck /> Seguimiento GLS</h3>
          </div>
          <div className="detail-card-body">
            {order.tracking_url ? (
              <>
                <div className={`tracking-status-block ${getShippingStatusClass(order.shipping_status)}`}>
                  <div className="tracking-icon-large">
                    {order.shipping_status === 'completed' || order.shipping_status === 'delivered' ? (
                      <FaCheckCircle />
                    ) : (
                      <FaTruck />
                    )}
                  </div>
                  <div className="tracking-info">
                    <span className="tracking-label-text">Estado del envío</span>
                    <span className="tracking-value-text">{order.shipping_label}</span>
                  </div>
                </div>

                <div className="detail-row tracking-number-row">
                  <span>Nº de seguimiento</span>
                  <span className="highlight">{order.gls_codbarras}</span>
                </div>

                {order.tiempo_estimado && (
                  <div className="detail-row">
                    <span><FaClock /> Entrega estimada</span>
                    <span>{order.tiempo_estimado}</span>
                  </div>
                )}

                <a
                  href={order.tracking_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="energy-btn primary tracking-btn"
                >
                  <FaExternalLinkAlt /> Ver seguimiento en GLS
                </a>
              </>
            ) : (
              <div className="empty-tracking">
                <FaTruck className="empty-icon" />
                <p>El seguimiento aún no está disponible.</p>
                <p className="empty-hint">Se activará cuando el pedido sea procesado.</p>
              </div>
            )}
          </div>
        </div>

        {/* Botón volver */}
        <button className="energy-btn secondary" onClick={() => navigate("/account?tab=orders")} style={{ width: '100%', marginTop: '1rem' }}>
          <FaArrowLeft /> Volver a Mis Pedidos
        </button>
      </div>
    </div>
  );
}